"use client";

import { ListSkeleton } from "@/components/ui/skeleton";
import {
  Message,
  MessageContent,
  MessageResponse,
} from "@/components/ai-elements/message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/page-header";
import { PanelLayout } from "@/components/ui/panel-layout";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { CheckIcon, CopyIcon, DownloadIcon, MessageSquareIcon, PanelLeftClose, PanelLeftOpen, RefreshCcwIcon, SearchIcon, SparklesIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

// ─── Types ────────────────────────────────────────────────────────────────────

type FeedbackStatus = "new" | "investigating" | "completed";
type FeedbackTargetKind = "chat" | "project-chat";

type FeedbackItem = {
  id: string;
  chat_id: string | null;
  project_chat_id: string | null;
  user_id: string;
  feedback: string;
  consent_to_read_chat: boolean;
  created_at: string;
  status: FeedbackStatus;
};

type MessagePart = { type: string; text?: string; [key: string]: unknown };

type AdminMessage = {
  id: string;
  content: string;
  parts: MessagePart[] | null;
  role: string;
  model: string;
  sequence_number: number;
  created_at: string;
  agent_id: string | null;
  image_paths: string[];
};

type ChatDetails = {
  id: string;
  name: string;
  created_at: string;
  model: string;
  agent_id: string | null;
  agent_name: string | null;
  active_tool_names: string[];
};

type FeedbackInfo = {
  user_id: string;
  submitter_name: string | null;
  feedback: string;
  consent_to_read_chat: boolean;
  created_at: string;
};

type AdminChatSuccessResponse = {
  success: true;
  chat_id: string;
  target_type: FeedbackTargetKind;
  chat_feedback: FeedbackInfo;
  chat_details: ChatDetails | null;
  messages: AdminMessage[];
  message_count: number;
  access_limited?: boolean;
};

type AdminChatErrorResponse = { error: string };
type AdminChatApiResponse = AdminChatSuccessResponse | AdminChatErrorResponse;

type SelectedChat = {
  chatId: string;
  targetKind: FeedbackTargetKind | null;
  feedbackId: string | null;
  feedbackStatus: FeedbackStatus;
  feedbackInfo: FeedbackInfo | null;
  chatDetails: ChatDetails | null;
  messages: AdminMessage[];
  accessLimited: boolean;
  loading: boolean;
  error: string | null;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getFeedbackTargetId(feedback: Pick<FeedbackItem, "chat_id" | "project_chat_id">): string | null {
  return feedback.project_chat_id ?? feedback.chat_id;
}

function getFeedbackTargetKind(
  feedback: Pick<FeedbackItem, "project_chat_id">
): FeedbackTargetKind {
  return feedback.project_chat_id ? "project-chat" : "chat";
}


function StatusBadge({ status }: { status: FeedbackStatus }) {
  if (status === "completed") {
    return (
      <Badge
        variant="secondary"
        className="shrink-0 bg-activated text-activated-foreground px-1.5 py-0 text-[10px]"
      >
        Completed
      </Badge>
    );
  }
  if (status === "investigating") {
    return (
      <Badge
        variant="outline"
        className="shrink-0 px-1.5 py-0 text-[10px] text-accent-foreground border-accent-foreground/30 bg-accent/40"
      >
        Investigating
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="shrink-0 px-1.5 py-0 text-[10px] text-muted-foreground"
    >
      New
    </Badge>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export type AdminChatsPanelProps = {
  onBack?: () => void;
};

type Analysis = {
  text: string;
  streaming: boolean;
  error: string | null;
};

export function AdminChatsPanel({ onBack }: AdminChatsPanelProps) {
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | "all">("all");
  const [selected, setSelected] = useState<SelectedChat | null>(null);
  const [reliability, setReliability] = useState<Analysis | null>(null);
  const [reliabilityOpen, setReliabilityOpen] = useState(false);
  const reliabilityAbortRef = useRef<AbortController | null>(null);

  const [chatIdInput, setChatIdInput] = useState("");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadFeedbacks = useCallback(async () => {
    setListLoading(true);
    setListError(null);

    const { data, error: queryError } = await supabase
      .from("chat_feedbacks")
      .select(
        "id, chat_id, project_chat_id, user_id, feedback, consent_to_read_chat, created_at, status"
      )
      .order("created_at", { ascending: false });

    setListLoading(false);

    if (queryError) {
      setListError(queryError.message);
      return;
    }

    setFeedbacks(((data as FeedbackItem[]) ?? []).filter((f) => !!getFeedbackTargetId(f)));
  }, []);

  useEffect(() => {
    void loadFeedbacks();
  }, [loadFeedbacks]);

  const filteredFeedbacks = useMemo(() => {
    const q = search.toLowerCase().trim();
    return feedbacks.filter((f) => {
      if (statusFilter !== "all" && f.status !== statusFilter) return false;
      if (!q) return true;
      const targetId = getFeedbackTargetId(f) ?? "";
      return (
        f.feedback.toLowerCase().includes(q) ||
        targetId.toLowerCase().includes(q)
      );
    });
  }, [feedbacks, search, statusFilter]);

  // Core fetch — works for both direct ID lookup and feedback list selection
  const fetchChat = useCallback(
    async (
      chatId: string,
      knownFeedbackId?: string,
      knownStatus?: FeedbackStatus,
      targetKind?: FeedbackTargetKind
    ) => {
      const trimmed = chatId.trim();
      if (!trimmed) return;

      setReliability(null);
      setSelected({
        chatId: trimmed,
        targetKind: targetKind ?? null,
        feedbackId: knownFeedbackId ?? null,
        feedbackStatus: knownStatus ?? "new",
        feedbackInfo: null,
        chatDetails: null,
        messages: [],
        accessLimited: false,
        loading: true,
        error: null,
      });

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const query = new URLSearchParams();
        query.set(targetKind === "project-chat" ? "project_chat_id" : "chat_id", trimmed);
        if (knownFeedbackId) query.set("feedback_id", knownFeedbackId);

        const url = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-admin-chat-messages?${query.toString()}`;
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${session?.access_token ?? ""}`,
            apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "",
          },
        });

        const json: AdminChatApiResponse = await res.json();

        if (!res.ok || !("success" in json)) {
          const msg =
            "error" in json ? json.error : "Failed to load chat messages.";
          setSelected((prev) =>
            prev ? { ...prev, loading: false, error: msg } : null
          );
          return;
        }

        // If we don't already know the feedback ID/status (direct lookup), fetch them
        let feedbackId = knownFeedbackId ?? null;
        let feedbackStatus: FeedbackStatus = knownStatus ?? "new";
        if (!feedbackId) {
          const { data: meta } = await supabase
            .from("chat_feedbacks")
            .select("id, status")
            .or(`chat_id.eq.${trimmed},project_chat_id.eq.${trimmed}`)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (meta) {
            feedbackId = meta.id as string;
            feedbackStatus = (meta.status as FeedbackStatus) ?? "new";
          }
        }

        setSelected({
          chatId: trimmed,
          targetKind: json.target_type,
          feedbackId,
          feedbackStatus,
          feedbackInfo: json.chat_feedback,
          chatDetails: json.chat_details,
          messages: json.messages,
          accessLimited: Boolean(json.access_limited),
          loading: false,
          error: null,
        });
      } catch (err) {
        setSelected((prev) =>
          prev
            ? {
                ...prev,
                loading: false,
                error: err instanceof Error ? err.message : "Unexpected error.",
              }
            : null
        );
      }
    },
    []
  );

  const handleSelectFeedback = useCallback(
    (feedback: FeedbackItem) => {
      const targetId = getFeedbackTargetId(feedback);
      if (!targetId) return;
      setChatIdInput(targetId);
      void fetchChat(
        targetId,
        feedback.id,
        feedback.status,
        getFeedbackTargetKind(feedback)
      );
      setMobileSidebarOpen(false);
    },
    [fetchChat]
  );

  const handleLookup = useCallback(() => {
    void fetchChat(chatIdInput);
  }, [fetchChat, chatIdInput]);

  const handleStatusChange = useCallback(
    async (feedbackId: string, newStatus: FeedbackStatus) => {
      const { error } = await supabase
        .from("chat_feedbacks")
        .update({ status: newStatus })
        .eq("id", feedbackId);

      if (error) return;

      setFeedbacks((prev) =>
        prev.map((f) => (f.id === feedbackId ? { ...f, status: newStatus } : f))
      );
      setSelected((prev) =>
        prev ? { ...prev, feedbackStatus: newStatus } : null
      );
    },
    []
  );

  const handleReliability = useCallback(async () => {
    if (!selected || selected.loading) return;

    reliabilityAbortRef.current?.abort();
    const controller = new AbortController();
    reliabilityAbortRef.current = controller;

    setReliability({ text: "", streaming: true, error: null });
    setReliabilityOpen(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch("/api/analyse-chat-reliability", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({
          chatId: selected.chatId,
          feedbackId: selected.feedbackId ?? undefined,
          targetKind: selected.targetKind ?? undefined,
          consentGiven: selected.feedbackInfo?.consent_to_read_chat ?? false,
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? `Request failed (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setReliability((prev) => prev ? { ...prev, text: prev.text + decoder.decode(value, { stream: true }) } : null);
      }
      setReliability((prev) => prev ? { ...prev, streaming: false } : null);
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      setReliability((prev) => prev ? { ...prev, streaming: false, error: err instanceof Error ? err.message : "Analysis failed." } : null);
    }
  }, [selected]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") handleLookup();
    },
    [handleLookup]
  );

  const sidebarContent = (
    <>
      <div className="border-b border-border p-3 flex flex-col gap-2">
        <Input
          placeholder="Filter feedback…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-sm"
        />
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as FeedbackStatus | "all")}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="investigating">Investigating</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1 overflow-y-auto">
        {listLoading ? (
          <ListSkeleton />
        ) : listError ? (
          <p className="p-4 text-sm text-destructive">{listError}</p>
        ) : filteredFeedbacks.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            No feedback entries found.
          </p>
        ) : (
          filteredFeedbacks.map((fb) => (
            <FeedbackListItem
              key={fb.id}
              feedback={fb}
              isSelected={selected?.chatId === getFeedbackTargetId(fb)}
              onSelect={handleSelectFeedback}
            />
          ))
        )}
      </div>
    </>
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
        <SheetContent className="w-72 p-0" side="left">
          <SheetTitle className="sr-only">Feedback list</SheetTitle>
          <SheetDescription className="sr-only">Filter and browse feedback chats.</SheetDescription>
          <div className="flex h-full flex-col">{sidebarContent}</div>
        </SheetContent>
      </Sheet>

      <div className="absolute right-0 top-0 z-10 flex items-center gap-0.5 p-2 sm:hidden">
        <button
          aria-label={mobileSidebarOpen ? "Close feedback list" : "Open feedback list"}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          onClick={() => setMobileSidebarOpen((o) => !o)}
          type="button"
        >
          {mobileSidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
        </button>
      </div>

      <PageHeader
        title="Feedback Chats"
        description="Inspect chats shared by users as part of their feedback submissions."
        onBack={onBack}
        actions={
          <div className="flex w-full items-center gap-2">
            <Input
              ref={inputRef}
              placeholder="Enter chat ID…"
              value={chatIdInput}
              onChange={(e) => setChatIdInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="h-9 font-mono text-sm"
              aria-label="Chat ID"
            />
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleLookup}
              disabled={!chatIdInput.trim()}
              className="shrink-0 gap-1.5"
            >
              <SearchIcon className="size-3.5" />
              Fetch
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => void loadFeedbacks()}
              disabled={listLoading}
              aria-label="Refresh feedback list"
              className="size-9 shrink-0"
            >
              <RefreshCcwIcon className="size-4" />
            </Button>
          </div>
        }
      />

      <PanelLayout
        sidebarClassName="hidden sm:flex w-72 p-0"
        rawContent
        sidebar={sidebarContent}
      >
        {selected === null ? (
          <EmptyState />
        ) : (
          <ChatViewer
            selected={selected}
            reliability={reliability}
            reliabilityOpen={reliabilityOpen}
            onReliabilityOpenChange={setReliabilityOpen}
            onReliability={handleReliability}
            onStatusChange={handleStatusChange}
          />
        )}
      </PanelLayout>
    </div>
  );
}

// ─── Admin message parts renderer ────────────────────────────────────────────

function AdminMessageParts({ msg }: { msg: AdminMessage }) {
  const [reasoningOpen, setReasoningOpen] = useState<Record<number, boolean>>(
    {}
  );

  const parts: MessagePart[] =
    msg.parts && msg.parts.length > 0
      ? msg.parts
      : [{ type: "text", text: msg.content }];

  return (
    <div className="flex flex-col gap-2">
      {parts.map((part, i) => {
        if (part.type === "text" && part.text) {
          return (
            <MessageResponse key={i} isAnimating={false}>
              {part.text}
            </MessageResponse>
          );
        }

        if (part.type === "reasoning" && part.text) {
          const open = reasoningOpen[i] ?? false;
          return (
            <div key={i} className="rounded-md border border-border bg-muted/40">
              <button
                type="button"
                onClick={() =>
                  setReasoningOpen((prev) => ({ ...prev, [i]: !open }))
                }
                className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="flex-1">Thinking</span>
                <span>{open ? "▲" : "▼"}</span>
              </button>
              {open && (
                <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground whitespace-pre-wrap">
                  {part.text}
                </div>
              )}
            </div>
          );
        }

        // Tool invocations (tool-* or dynamic-tool)
        if (part.type === "dynamic-tool" || part.type.startsWith("tool-")) {
          const toolName = part.type === "dynamic-tool"
            ? String((part as MessagePart).toolName ?? "tool")
            : part.type.replace(/^tool-/, "");
          const state = String((part as MessagePart).state ?? "");
          return (
            <div
              key={i}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5"
            >
              <Badge variant="outline" className="text-[10px] shrink-0">
                {toolName}
              </Badge>
              {state && (
                <span className="text-[10px] text-muted-foreground">{state}</span>
              )}
            </div>
          );
        }

        // File / image parts
        if (part.type === "file") {
          return (
            <div
              key={i}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5"
            >
              <Badge variant="outline" className="text-[10px]">file</Badge>
              <span className="text-[10px] text-muted-foreground truncate">
                {String((part as MessagePart).name ?? (part as MessagePart).mediaType ?? "attachment")}
              </span>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

// ─── Feedback list item ───────────────────────────────────────────────────────

function FeedbackListItem({
  feedback,
  isSelected,
  onSelect,
}: {
  feedback: FeedbackItem;
  isSelected: boolean;
  onSelect: (feedback: FeedbackItem) => void;
}) {
  const date = useMemo(
    () =>
      new Intl.DateTimeFormat("en-NZ", {
        dateStyle: "short",
        timeStyle: "short",
        timeZone: "Pacific/Auckland",
      }).format(new Date(feedback.created_at)),
    [feedback.created_at]
  );

  return (
    <button
      type="button"
      onClick={() => onSelect(feedback)}
      className={cn(
        "flex w-full flex-col items-start gap-1 border-b border-border px-3 py-3 text-left transition-colors hover:bg-accent/60",
        isSelected && "bg-accent text-accent-foreground"
      )}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <span className="truncate font-mono text-[10px] text-muted-foreground">
          {getFeedbackTargetId(feedback)?.slice(0, 8) ?? "unknown"}…
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {feedback.project_chat_id ? (
            <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
              Project
            </Badge>
          ) : null}
          <StatusBadge status={feedback.status ?? "new"} />
        </div>
      </div>
      <p className="line-clamp-2 text-xs text-foreground">{feedback.feedback}</p>
      <div className="flex w-full items-center justify-between gap-2">
        <p className="text-[10px] text-muted-foreground">{date} NZT</p>
        {feedback.consent_to_read_chat ? (
          <Badge
            variant="secondary"
            className="shrink-0 bg-activated text-activated-foreground px-1.5 py-0 text-[10px]"
          >
            Consent
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="shrink-0 px-1.5 py-0 text-[10px] text-muted-foreground"
          >
            No consent
          </Badge>
        )}
      </div>
    </button>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
      <MessageSquareIcon className="size-8 text-muted-foreground/30" />
      <p className="text-sm text-muted-foreground">
        Enter a chat ID above or select a feedback entry
      </p>
    </div>
  );
}

// ─── Chat viewer ──────────────────────────────────────────────────────────────

function ChatViewer({
  selected,
  reliability,
  reliabilityOpen,
  onReliabilityOpenChange,
  onReliability,
  onStatusChange,
}: {
  selected: SelectedChat;
  reliability: Analysis | null;
  reliabilityOpen: boolean;
  onReliabilityOpenChange: (open: boolean) => void;
  onReliability: () => void;
  onStatusChange: (feedbackId: string, status: FeedbackStatus) => Promise<void>;
}) {
  const [copied, setCopied] = useState(false);
  const [sheetWidth, setSheetWidth] = useState(640);
  const isDragging = useRef(false);

  const handleResizePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    isDragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handleResizePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const newWidth = window.innerWidth - e.clientX;
    setSheetWidth(Math.min(Math.max(newWidth, 360), window.innerWidth - 80));
  }, []);

  const handleResizePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    isDragging.current = false;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  }, []);

  const handleCopy = useCallback(() => {
    if (!reliability?.text) return;
    void navigator.clipboard.writeText(reliability.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [reliability]);

  const handleDownload = useCallback(() => {
    if (!reliability?.text) return;
    const blob = new Blob([reliability.text], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reliability-${selected.chatId.slice(0, 8)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [reliability, selected.chatId]);
  const { feedbackInfo, chatDetails, messages, accessLimited, loading, error } =
    selected;

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <ListSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <p className="max-w-sm text-center text-sm text-destructive">{error}</p>
      </div>
    );
  }

  const enabledFeatures = chatDetails?.active_tool_names ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Metadata header */}
      <div className="shrink-0 space-y-3 border-b border-border p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="truncate text-sm font-semibold text-foreground">
            {chatDetails?.name ?? selected.chatId}
          </h2>
          <div className="flex shrink-0 items-center gap-2">
            {selected.feedbackId && (
              <Select
                value={selected.feedbackStatus}
                onValueChange={(v) =>
                  void onStatusChange(selected.feedbackId!, v as FeedbackStatus)
                }
              >
                <SelectTrigger className="h-8 w-36 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onReliability}
              disabled={reliability?.streaming ?? false}
              className="gap-1.5"
            >
              <SparklesIcon size={14} />
              {reliability?.streaming ? "Analysing…" : "Analyse"}
            </Button>
          </div>
        </div>

        {/* Feature badges */}
        <div className="flex flex-wrap gap-1.5">
          {selected.targetKind === "project-chat" ? (
            <Badge variant="secondary" className="text-xs">
              Project
            </Badge>
          ) : null}
          {chatDetails?.model && (
            <Badge variant="outline" className="text-xs">
              {chatDetails.model}
            </Badge>
          )}
          {chatDetails?.agent_name && (
            <Badge variant="secondary" className="text-xs">
              Agent: {chatDetails.agent_name}
            </Badge>
          )}
          {enabledFeatures.map((feat) => (
            <Badge
              key={feat}
              variant="secondary"
              className="bg-activated text-activated-foreground text-xs"
            >
              {feat}
            </Badge>
          ))}
        </div>

        {/* Feedback quote (only if available) */}
        {feedbackInfo?.feedback && (
          <div className="rounded-md border border-border bg-muted/50 p-3 space-y-2">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
              User feedback
            </p>
            <p className="text-sm text-foreground">{feedbackInfo.feedback}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
              {feedbackInfo.submitter_name && (
                <span>
                  Submitted by{" "}
                  <span className="font-medium text-foreground">
                    {feedbackInfo.submitter_name}
                  </span>
                </span>
              )}
              <span>
                {new Intl.DateTimeFormat("en-NZ", {
                  dateStyle: "medium",
                  timeStyle: "short",
                  timeZone: "Pacific/Auckland",
                }).format(new Date(feedbackInfo.created_at))}{" "}
                NZT
              </span>
            </div>
          </div>
        )}

        {accessLimited && (
          <div className="rounded-md border border-border bg-muted/50 p-3">
            <p className="text-xs text-muted-foreground">
              User did not consent to chat review. Showing feedback note only.
            </p>
          </div>
        )}
      </div>

      {/* Body: messages + analysis side by side */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Message list */}
        <div className="flex-1 overflow-y-auto">
          {messages.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No messages found.
            </p>
          ) : (
            <div className="mx-auto flex w-full max-w-3xl flex-col gap-2 px-4 py-6">
              {messages.map((msg) => (
                <Message key={msg.id} from={msg.role as "user" | "assistant"}>
                  <MessageContent>
                    <AdminMessageParts msg={msg} />
                  </MessageContent>
                </Message>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reliability analysis — wider Sheet */}
      <Sheet open={reliabilityOpen} onOpenChange={onReliabilityOpenChange}>
        <SheetContent
          side="right"
          className="flex flex-col gap-0 p-0 transition-none"
          style={{ width: sheetWidth, maxWidth: "none" }}
        >
          {/* Drag-to-resize handle */}
          <div
            className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/20 active:bg-primary/30"
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
          />
          <SheetHeader className="shrink-0 border-b border-border px-6 pt-4 pb-3">
            <div className="flex items-center gap-2 pr-8">
              <SparklesIcon size={16} className="shrink-0 text-muted-foreground" />
              <SheetTitle className="text-sm">Chat Analysis</SheetTitle>
            </div>
            <SheetDescription className="text-xs">
              {selected.feedbackInfo?.consent_to_read_chat
                ? `Full content analysis — chat ${selected.chatId.slice(0, 8)}… (user consented)`
                : `Structural analysis — chat ${selected.chatId.slice(0, 8)}… (no message content)`}
            </SheetDescription>
            <div className="flex items-center gap-1.5 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                disabled={!reliability?.text || reliability.streaming}
                className="h-7 gap-1.5 px-2.5 text-xs"
              >
                {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={!reliability?.text || reliability.streaming}
                className="h-7 gap-1.5 px-2.5 text-xs"
              >
                <DownloadIcon size={12} />
                Download .md
              </Button>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
            {reliability?.error ? (
              <p className="text-sm text-destructive">{reliability.error}</p>
            ) : reliability ? (
              <MessageResponse isAnimating={reliability.streaming}>
                {reliability.text}
              </MessageResponse>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
